import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import SchoolDocumentHeader, { schoolLogoFor } from "@/components/SchoolDocumentHeader";
import { choiceText, MEETING_STATUSES } from "@/components/MeetingChoices";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import PrintSignatures from "@/components/PrintSignatures";
import { staffTitles } from "@/lib/staffTitles";

export function useCouncilPrint() {
  const [request, setRequest] = useState(null);
  const finish = useCallback(() => setRequest(null), []);
  return { request, print: (meeting = null) => setRequest({ meeting }), finish };
}

export default function CouncilRecordPrint({ request, onFinish, title, schoolKey, schoolName, counselorName, counselorGender, counselorId, members, meetings }) {
  const container = useRef(null);
  const [principalName, setPrincipalName] = useState("");
  const [principalGender, setPrincipalGender] = useState("");
  useEffect(() => {
    if (!request) return undefined;
    let cancelled = false;
    const cleanup = () => {
      document.body.classList.remove("printing-council-record");
      onFinish();
    };
    window.addEventListener("afterprint", cleanup);
    const start = async () => {
      try {
        if (!schoolLogoFor(schoolKey)) throw new Error("لا يمكن الطباعة قبل تحديد مدرسة لها شعار معتمد.");
        const { data } = await api.get("/school-principal", { params: counselorId ? { counselor_id: counselorId } : {} });
        if (cancelled) return;
        flushSync(() => { setPrincipalName(data.name || ""); setPrincipalGender(data.gender || ""); });
        await Promise.all([...container.current.querySelectorAll("img")].map((image) => image.decode()));
        if (document.fonts?.ready) await document.fonts.ready;
        if (cancelled) return;
        document.body.classList.add("printing-council-record");
        window.print();
      } catch (error) {
        if (!cancelled) {
          toast.error(formatErr(error));
          cleanup();
        }
      }
    };
    start();
    return () => {
      cancelled = true;
      window.removeEventListener("afterprint", cleanup);
      document.body.classList.remove("printing-council-record");
    };
  }, [request, onFinish, schoolKey, counselorId]);

  if (!request) return null;
  const selectedMeetings = request.meeting ? [request.meeting] : meetings;
  const header = <SchoolDocumentHeader schoolKey={schoolKey} gender={counselorGender} schoolName={schoolName} />;
  const signatures = <PrintSignatures className="council-print-signature" counselorName={counselorName} principalName={principalName} counselorGender={counselorGender} principalGender={principalGender} />;
  return createPortal(
    <div className="council-record-print" ref={container} dir="rtl" data-testid="council-print-document">
      {!request.meeting && (
        <section className="council-print-page">
          {header}
          <h1>{title}</h1>
          <p>{staffTitles(counselorGender).counselor}: {counselorName || "—"}</p>
          <h2>الأعضاء</h2>
          <table className="council-print-table">
            <thead><tr><th>م</th><th>الاسم</th><th>الدور / الصف</th></tr></thead>
            <tbody>{members.map((member, index) => <tr key={member.id}><td>{index + 1}</td><td>{member.name}</td><td>{member.detail}</td></tr>)}</tbody>
          </table>
          {!members.length && <p>لا يوجد أعضاء مسجلون.</p>}
          <p>عدد الاجتماعات: {meetings.length}</p>
          {signatures}
        </section>
      )}
      {selectedMeetings.map((meeting) => (
        <section className="council-print-page" key={meeting.id}>
          {header}
          <h1>محضر اجتماع — {title}</h1>
          <p>التاريخ: {meeting.data.date || "—"}　 الوقت: {meeting.data.time || "—"}</p>
          <p>المكان: {meeting.data.location || "—"}</p>
          <p>الحالة: {MEETING_STATUSES.find((status) => status.value === meeting.data.status)?.label || "—"}</p>
          <h2>الموضوعات والمحاور</h2>
          <p className="council-print-text">{choiceText(meeting.data.topics || meeting.data.agenda) || "—"}</p>
          <h2>القرارات والتوصيات</h2>
          <p className="council-print-text">{choiceText(meeting.data.decisions) || "—"}</p>
          <h2>الحضور</h2>
          <table className="council-print-table">
            <thead><tr><th>الاسم</th><th>الدور / الصف</th></tr></thead>
            <tbody>{(meeting.data.attendees || []).map((attendee) => (
              <tr key={attendee.member_id || attendee.student_id}><td>{attendee.name}</td><td>{attendee.role_label || attendee.grade || "—"}</td></tr>
            ))}</tbody>
          </table>
          {signatures}
        </section>
      ))}
    </div>,
    document.body,
  );
}
